import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { DefaultOptionType } from 'antd/es/select';
import Select from 'antd/es/select';
import { useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import type { AllowedTestCase } from 'utils/sandbox/allowedPayoutsTestCases';
import { payoutStateAllowedTestCaseMap } from 'utils/sandbox/allowedPayoutsTestCases';
import { withSandbox } from 'utils/withSandbox';

import type { PutSandboxPayoutsUniqueReferenceTestCaseBodyTestCase as TestCase } from 'api/sandboxApi';
import { usePutSandboxPayoutsUniqueReferenceTestCase } from 'api/sandboxApi';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

import { SandboxAlert } from './SandboxAlert';
import { sandboxT } from './translations';

type Props = {
  currentStatus: string;
  uniqueReference: string;
  onSuccess: VoidFunction;
};

const testCaseLabels: Record<AllowedTestCase, string> = {
  success: sandboxT.payoutTestCases.labels.success,
  returned: sandboxT.payoutTestCases.labels.returned
};

const SandboxPayoutDetailsTestCaseSection = withSandbox(
  ({ currentStatus, uniqueReference, onSuccess }: Props) => {
    const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(
      null
    );

    const organisationId = useOrganisationId<number>();
    const openNotification = useOpenNotification();
    const { mutate, isPending, isSuccess } =
      usePutSandboxPayoutsUniqueReferenceTestCase();

    const availableTestCases: TestCase[] =
      payoutStateAllowedTestCaseMap[currentStatus] || [];

    const options: DefaultOptionType[] = availableTestCases.map((testCase) => ({
      label: testCaseLabels[testCase],
      value: testCase
    }));

    const disabled = isPending || isSuccess;

    const alertMessage = availableTestCases.length
      ? sandboxT.payoutTestCases.alert.withTestCases
      : sandboxT.payoutTestCases.alert.restricted;

    const executeTestCase = () => {
      if (selectedTestCase) {
        mutate(
          {
            organisationId,
            uniqueReference,
            data: {
              test_case: selectedTestCase
            }
          },
          {
            onSuccess: () => {
              openNotification({
                kind: 'success',
                message: sandboxT.common.notifications.testCaseExecuted
              });
              onSuccess();
            },
            onError: (err) => {
              // err type is misleading, in case of 5xx errors it's just a string
              openNotification({
                kind: 'error',
                message:
                  err.error?.title || sandboxT.common.notifications.serverError
              });
            }
          }
        );
      }
    };

    return (
      <div className="flex flex-col gap-6">
        <SandboxAlert
          message={
            <>
              {alertMessage} {sandboxT.common.documentation.see}{' '}
              <a href="/docs/guide/sandbox#sandbox-payouts">
                {sandboxT.common.documentation.label}
              </a>{' '}
              {sandboxT.common.documentation.forMoreDetails}
            </>
          }
        />
        {!!availableTestCases.length && (
          <div className="flex gap-4">
            <Select
              disabled={disabled}
              className="grow"
              size="large"
              placeholder={sandboxT.common.placeholders.selectTestCase}
              suffixIcon={<ChevronDownIcon width={20} height={20} />}
              options={options}
              value={selectedTestCase}
              onSelect={setSelectedTestCase}
              data-testid="payouts-details-sandbox-test-case-dropdown"
            />
            <PrimaryButton
              className="shrink-0"
              disabled={!selectedTestCase || disabled}
              onClick={executeTestCase}
            >
              {sandboxT.common.actions.execute}
            </PrimaryButton>
          </div>
        )}
      </div>
    );
  }
);

export { SandboxPayoutDetailsTestCaseSection };
